import { identity } from "../../../lib/utils/Identity.js";
import { PromiseOnSteroids } from "../../../lib/utils/PromiseOnSteroids.js";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";
import { AdUnit } from "../AdUnit.js";
import { AdCallParams } from "../AdCallParams.js";

/**
 * this interface represents 3rd party ad services providers, like
 * Amazon A9 (TAM) ad IAS 4 brand safety, that typically require
 * specific client side sdks to communicate with their systems in order
 * to receive descriptive codes/strings that should be sent to your SSP 
 * as AdCall parameters.
 * So this modules should be before the adcall.
 */
export interface PrebidModule {




    /**
     * this method could be used to preload the required resources.
     * [[bidAndEnrich]] waits for resources to be loaded (aka the returning promise to be completed),
     * in order to perform the bids.
     * this method could be eagerly called, in a fire and forget fashion, to speed-up first bid request
     * on the first ad call
     * @returns a completed promise on load completion
     */
    load(): Promise<void>


    
    /**
     * this method performs provider specific operations and requests to achieve the "bid"
     * and eventually returns an ad call parameter enricher.
     * Returning an enricher instead of directly enriched parameters is a tecnique
     * to enable request paralellization (when using multiple modules each representing a different provider)
     * while keeping serializable the process of adparams enrichment. 
     * @param unit slotOrUnitNames to ask the bid for
     * @param info current video information and metadata
     */
    bidAndGetEnricher(adUnit: AdUnit, info: VideoInfoRawData): Promise<AdParamsEnricher>;






}

/**
 * this function transform and enrich [[AdCallParams]]
 */
export type AdParamsEnricher = (x: AdCallParams) => AdCallParams;






export abstract class AbstractPrebidModule <ProviderSdk>{



    
    private loadPromise: Promise<ProviderSdk>;


    /**
     * this method is called once,
     * and should return the required sdk
     */
    protected abstract internalLoadOps(): Promise<ProviderSdk>;


    /**
     * 
     * @param bidTimeout non positive numbers means no timeout at all. Timeout should be expressed in milliseconds
     */
    constructor(private bidTimeout: number = 1000) {
        
    }


    private iLoad(): Promise<ProviderSdk> {
        if(!this.loadPromise){
            this.loadPromise = this.internalLoadOps();
        }
        return this.loadPromise;
    }


    public load(): Promise<void> {
        return this.iLoad().then();
    }


    public abstract get providerName();

    


    protected abstract internalBidOps(sdk: ProviderSdk, adUnit: AdUnit, info: VideoInfoRawData): Promise<AdParamsEnricher>;


    protected fallbackAdParamsEnricher: AdParamsEnricher = (params: AdCallParams) => {
        return params;
    }


    bidAndGetEnricher(adUnit: AdUnit, info: VideoInfoRawData): Promise<AdParamsEnricher> {
        console.debug("HB-" + this.providerName, "Starting");
        return Promise.race([
            this.iLoad().then(sdk => this.internalBidOps(sdk,adUnit,info)).catch(err => {
                console.warn("HB-" + this.providerName,err);
                return Promise.resolve(identity);
            }),
            PromiseOnSteroids.timeout<AdParamsEnricher>(this.bidTimeout, (params: AdCallParams) => {
                console.warn("HB-" + this.providerName, "timeout occurred. Fallback AdParamsEnricher");
                return this.fallbackAdParamsEnricher(params)
            })
        ]);
    }



}